import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "../../../components/Buttons/Button";
import AdMediaPreview from "../../../components/Common/AdMediaPreview";
import { getCreativePreviewMediaUrls } from "../../../components/Common/adMediaPreviewUtils";
import ModalMedia from "../../../components/Modal/ModalMedia";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CAMPAIGN_APPROVAL_CREATIVES_TITLE,
  CAMPAIGN_APPROVAL_CREATIVE_TITLE,
  CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON,
  CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_PLACEHOLDER,
  CAMPAIGN_APPROVAL_TEXTS_TITLE,
  CAROUSEL,
} from "../../../i18n/keysTranslations";
import CampaignApprovalCommentBody from "./CampaignApprovalCommentBody";
import CommentForm from "./CommentForm";
import {
  getCampaignApprovalCreativeItemLabel,
  getCampaignApprovalFieldLabel,
  getCampaignApprovalTargetKey,
  getCommentsForTarget,
  getCreativeCommentTarget,
  isCarouselApprovalPlatform,
  isCarouselCreativesSection,
} from "../utils";
import {
  getApprovalEntityPlatform,
  isApprovalAdPreviewSupported,
} from "../utils/adPreviewFormats";
import CampaignApprovalAdPreview from "./CampaignApprovalAdPreview";
import ApprovalDestination from "./ApprovalDestination";
import { format } from "date-fns";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";

const ApprovalEntityCard = ({
  token = "",
  campaignID = "",
  entity,
  isExpanded = false,
  comments = [],
  currentDraftID = "",
  commentsLocked = false,
  isSubmittingComment = false,
  onSubmitSpecificComment,
}) => {
  const { t } = useTranslationApp();
  const [activeTargetKey, setActiveTargetKey] = useState("");
  const [selectedCreative, setSelectedCreative] = useState(null);
  const creatives = useMemo(() => entity?.creatives || [], [entity?.creatives]);
  const isCarouselSection = isCarouselCreativesSection(creatives);
  const isSingularCreativeSection =
    creatives.length === 1 && !isCarouselSection;
  const creativePlatform = creatives[0]?.target?.platform;
  const showCarouselChip =
    isCarouselSection && isCarouselApprovalPlatform(creativePlatform);
  const creativesSectionTitle = isSingularCreativeSection
    ? CAMPAIGN_APPROVAL_CREATIVE_TITLE
    : CAMPAIGN_APPROVAL_CREATIVES_TITLE;
  const showAdPreview = isApprovalAdPreviewSupported(entity);
  const previewPlatform = getApprovalEntityPlatform(entity);
  const previewMediaUrl = creatives[0]?.url || "";
  const singularCreativeTarget = useMemo(() => {
    if (!isSingularCreativeSection) {
      return null;
    }

    return getCreativeCommentTarget(creatives[0]?.target);
  }, [creatives, isSingularCreativeSection]);

  const onToggleSpecificComment = (target) => {
    const nextKey = getCampaignApprovalTargetKey(target);
    setActiveTargetKey((currentKey) => (currentKey === nextKey ? "" : nextKey));
  };

  const renderCreativeMedia = (creative) => {
    const mediaUrls = getCreativePreviewMediaUrls(creative);

    return (
      <AdMediaPreview
        mediaUrls={mediaUrls}
        maxWidth={360}
        onSelectPreview={() => setSelectedCreative(creative)}
      />
    );
  };

  const renderCreativesList = () => (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {creatives.map((creative, index) => {
        const targetKey = getCampaignApprovalTargetKey(creative.target);
        const fieldLabel = getCampaignApprovalCreativeItemLabel({
          creative,
          isCarouselSection,
          isSingularCreativeSection,
          t,
        });
        const isLast = index === creatives.length - 1;
        const showItemLabel = Boolean(fieldLabel);

        return (
          <Box
            key={targetKey}
            sx={{
              py: 1.5,
              borderBottom: isLast ? "none" : `1px solid ${INPUT_COLOR_BORDER}`,
            }}
          >
            {showItemLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  display: "block",
                  mb: 1,
                }}
              >
                {fieldLabel}
              </Typography>
            ) : null}

            {renderCreativeMedia(creative)}
          </Box>
        );
      })}
    </Box>
  );

  const creativeModalItem = useMemo(() => {
    if (!selectedCreative) {
      return null;
    }

    const carouselImages = (selectedCreative?.urls || []).map((url) => ({
      url,
    }));

    return {
      isCarousel: Boolean(selectedCreative?.isCarousel),
      carouselImages,
      _initialCarouselIndex: selectedCreative?.mediaIndex || 0,
    };
  }, [selectedCreative]);

  const renderItemComments = (target) => {
    const itemComments = getCommentsForTarget(comments, target, currentDraftID);

    if (!itemComments.length) {
      return null;
    }

    return (
      <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
        {itemComments.map((comment) => (
          <Box
            key={comment.id}
            sx={{
              borderLeft: "3px solid",
              borderColor: "primary.main",
              pl: 1.25,
              py: 0.5,
            }}
          >
            <CampaignApprovalCommentBody body={comment.body} />
            {comment?.reviewerEmail || comment?.createdAt ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                {comment?.reviewerEmail && (
                  <Typography variant="caption" color="text.secondary">
                    {comment.reviewerEmail}
                  </Typography>
                )}
                {comment?.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(comment.createdAt), "dd MMM yyyy, HH:mm")}
                  </Typography>
                )}
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  const renderSpecificCommentForm = ({ target }) => {
    if (activeTargetKey !== getCampaignApprovalTargetKey(target)) {
      return null;
    }

    return (
      <CommentForm
        inline={true}
        placeholder={CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_PLACEHOLDER}
        submitLabel={CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON}
        disabled={commentsLocked}
        loading={isSubmittingComment}
        onCancel={() => setActiveTargetKey("")}
        onSubmit={async ({ body }) =>
          onSubmitSpecificComment?.({ body, target })
        }
      />
    );
  };

  const renderCreativeCommentPreview = (creative) => {
    const mediaUrls = getCreativePreviewMediaUrls(creative);

    return (
      <AdMediaPreview
        mediaUrls={mediaUrls}
        maxWidth={80}
        onSelectPreview={() => setSelectedCreative(creative)}
        sx={{
          width: 80,
          height: 80,
          "& img, & video": {
            objectFit: "cover",
            width: "100%",
            height: "100%",
          },
        }}
      />
    );
  };

  const renderCreativeCommentBlock = ({ creative, index }) => {
    const commentTarget = getCreativeCommentTarget(creative.target);
    const targetKey = getCampaignApprovalTargetKey(commentTarget);
    const fieldLabel = getCampaignApprovalCreativeItemLabel({
      creative,
      isCarouselSection,
      isSingularCreativeSection,
      t,
    });
    const isLast = index === creatives.length - 1;
    const showItemLabel = Boolean(fieldLabel);

    return (
      <Box
        key={`comment-${targetKey}`}
        sx={{
          py: 1.5,
          borderBottom: isLast ? "none" : `1px solid ${INPUT_COLOR_BORDER}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            {showItemLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {fieldLabel}
              </Typography>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}

            {!commentsLocked ? (
              <Button
                type="button"
                color="secondary"
                sx={{
                  minWidth: "max-content",
                  height: 36,
                  px: 2,
                  flexShrink: 0,
                }}
                onClick={() => onToggleSpecificComment(commentTarget)}
              >
                {t(CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON)}
              </Button>
            ) : null}
          </Box>

          {renderCreativeCommentPreview(creative)}

          {renderItemComments(commentTarget)}
          {renderSpecificCommentForm({ target: commentTarget })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {entity?.destination ? (
          <Box sx={{ mb: 2 }}>
            <ApprovalDestination
              platform={previewPlatform}
              destination={entity.destination}
            />
            <Divider sx={{ mt: 2, borderColor: INPUT_COLOR_BORDER }} />
          </Box>
        ) : null}

        {entity?.texts?.length > 0 ? (
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "text.secondary",
                display: "block",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              {t(CAMPAIGN_APPROVAL_TEXTS_TITLE)}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {entity.texts.map((textEntry, index) => {
                const targetKey = getCampaignApprovalTargetKey(
                  textEntry.target
                );
                const fieldLabel = getCampaignApprovalFieldLabel(
                  textEntry.fieldType,
                  t,
                  textEntry.fieldIndex
                ).toUpperCase();
                const isLast = index === entity.texts.length - 1;

                return (
                  <Box
                    key={targetKey}
                    sx={{
                      py: 1.5,
                      borderBottom: isLast
                        ? "none"
                        : `1px solid ${INPUT_COLOR_BORDER}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.6875rem",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            display: "block",
                          }}
                        >
                          {fieldLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.35, whiteSpace: "pre-wrap" }}
                        >
                          {textEntry.value}
                        </Typography>
                      </Box>

                      {!commentsLocked ? (
                        <Button
                          type="button"
                          color="secondary"
                          sx={{
                            minWidth: "max-content",
                            height: 36,
                            px: 2,
                            flexShrink: 0,
                          }}
                          onClick={() =>
                            onToggleSpecificComment(textEntry.target)
                          }
                        >
                          {t(CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON)}
                        </Button>
                      ) : null}
                    </Box>

                    {renderItemComments(textEntry.target)}
                    {renderSpecificCommentForm({ target: textEntry.target })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {entity?.creatives?.length > 0 ? (
          <Box sx={{ mt: entity?.texts?.length > 0 ? 2 : 0 }}>
            {entity?.texts?.length > 0 ? (
              <Divider sx={{ mb: 2, borderColor: INPUT_COLOR_BORDER }} />
            ) : null}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  {t(creativesSectionTitle)}
                </Typography>
                {showCarouselChip ? (
                  <Chip
                    label={t(CAROUSEL)}
                    size="small"
                    sx={{
                      backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                    }}
                  />
                ) : null}
              </Box>

              {isSingularCreativeSection && !commentsLocked ? (
                <Button
                  type="button"
                  color="secondary"
                  sx={{
                    minWidth: "max-content",
                    height: 36,
                    px: 2,
                    flexShrink: 0,
                  }}
                  onClick={() =>
                    onToggleSpecificComment(singularCreativeTarget)
                  }
                >
                  {t(CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON)}
                </Button>
              ) : null}
            </Box>

            {isSingularCreativeSection ? (
              <Box
                sx={{
                  mb:
                    activeTargetKey ===
                    getCampaignApprovalTargetKey(singularCreativeTarget)
                      ? 1.5
                      : 0,
                }}
              >
                {renderSpecificCommentForm({ target: singularCreativeTarget })}
              </Box>
            ) : null}

            {showAdPreview ? (
              <CampaignApprovalAdPreview
                token={token}
                campaignID={campaignID}
                platform={previewPlatform}
                entityID={entity?.entityID || ""}
                isExpanded={isExpanded}
                isCarousel={Boolean(creatives[0]?.isCarousel)}
                mediaUrl={previewMediaUrl}
                fallback={renderCreativesList()}
              />
            ) : (
              renderCreativesList()
            )}

            {isSingularCreativeSection ? (
              renderItemComments(singularCreativeTarget)
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", mt: 1.5 }}>
                {creatives.map((creative, index) =>
                  renderCreativeCommentBlock({ creative, index })
                )}
              </Box>
            )}
          </Box>
        ) : null}
      </Box>

      {selectedCreative ? (
        <ModalMedia
          modalOpen={Boolean(selectedCreative)}
          onCloseModal={() => setSelectedCreative(null)}
          url={selectedCreative.url}
          item={creativeModalItem}
          showEditButton={false}
          showDownloadButton={false}
          showShareButton={false}
          showDeleteButton={false}
        />
      ) : null}
    </>
  );
};

export default ApprovalEntityCard;
